import { useAppSelector } from 'store/store-hooks';
import { selectClipData, isNull } from 'components/app/_helpers/_app.helper';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { getTsSliderActions, linkedActions } from './timestamp.helper';

type TsActions = {
  startTimeEnabled: boolean;
  endTimeEnabled: boolean;
  splitEnabled: boolean;
  removeStartTimeEnabled: boolean;
  removeEndTimeEnabled: boolean;
};

export const useTimestampActions = () => {
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const tsLinkedPayload = useAppSelector((state) => state.timestamp.tsLinkedPayload);
  const playerDuration = useAppSelector((state) => state.timestamp.currentPlayerDuration);
  const beginnerMode = useAppSelector((state) => state.app.beginnerMode);

  const getLinkedActions = (playerTime: EditorTime, clip: Clip) => {
    if (!tsLinkedPayload) return;
    const { canSetStartTime, canSetEndTime, canSplit, canRemoveEndTime, canRemoveStartTime } = linkedActions(
      clip,
      playerTime,
      tsLinkedPayload
    );
    return {
      startTimeEnabled: canSetStartTime(),
      endTimeEnabled: canSetEndTime(),
      splitEnabled: canSplit(),
      removeStartTimeEnabled: canRemoveStartTime(),
      removeEndTimeEnabled: canRemoveEndTime(),
    };
  };

  const getUnlinkedActions = (playerTime: EditorTime, clip: Clip) => {
    const res = {
      startTimeEnabled: false,
      endTimeEnabled: false,
      splitEnabled: false,
      removeStartTimeEnabled: false,
      removeEndTimeEnabled: false,
    };
    const actions = getTsSliderActions(clip, tsLinkedPayload, playerDuration, beginnerMode);
    if (!isNull(clip.startTime) && isNull(clip.endTime)) {
      if (playerTime === clip.startTime) res.endTimeEnabled = actions.end;
      else if (playerTime > clip.startTime) {
        res.startTimeEnabled = actions.start;
        res.endTimeEnabled = actions.end;
        res.splitEnabled = actions.start && actions.end;
      } else res.startTimeEnabled = actions.start;
    } else if (isNull(clip.startTime) && !isNull(clip.endTime)) {
      if (playerTime === clip.endTime) res.startTimeEnabled = actions.start;
      else if (playerTime < clip.endTime) {
        res.startTimeEnabled = actions.start;
        res.endTimeEnabled = actions.end;
        res.splitEnabled = actions.start && actions.end;
      } else res.endTimeEnabled = actions.end;
    } else if (!isNull(clip.startTime) && !isNull(clip.endTime)) {
      if (playerTime > clip.startTime && playerTime < clip.endTime) {
        res.startTimeEnabled = actions.start;
        res.endTimeEnabled = actions.end;
        res.splitEnabled = actions.start && actions.end;
      } else if (playerTime < clip.startTime) res.startTimeEnabled = actions.start;
      else if (playerTime > clip.endTime) res.endTimeEnabled = actions.end;
      res.removeStartTimeEnabled = true;
      res.removeEndTimeEnabled = true;
    }
    return res;
  };

  return (playerTime: EditorTime, editingClip: ClipId): TsActions | undefined => {
    if (!clipsMap) return;
    const clip = selectClipData(editingClip, clipsMap);
    if (clip.clipGroupId) return getLinkedActions(playerTime, clip);
    return getUnlinkedActions(playerTime, clip);
  };
};
